import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/component-page.default.js';

const VALID_TABS = ['usage', 'style', 'code', 'accessibility'];

class RunComponentPage extends skinResolutionMixin(RunElement) {
  // ── Private state ──────────────────────────────────────────────────────────
  #src = '';
  #activeTab = 'usage';
  #tabLabelUsage = 'Usage';
  #tabLabelStyle = 'Style';
  #tabLabelCode = 'Code';
  #tabLabelAccessibility = 'Accessibility';
  #manifest = null;
  #loadState = 'idle'; // 'idle' | 'loading' | 'loaded' | 'error'
  #errorStatus = 0;
  #errorMessage = '';
  #abortController = null;
  #slotListenersAttached = false;

  // ── Skin registry ──────────────────────────────────────────────────────────
  // component-page is a STRUCTURAL compound: it composes run-tabs / run-badge /
  // run-table, and each of those resolves and renders its own skin (skin
  // resolution crosses shadow boundaries — RUN-514). So the compound needs only
  // one skin; it does not reimplement itself per skin. A removed `wireframe`
  // skin loader resolves to defaultSkin via the mixin's fallback, and the
  // composed children supply the wireframe look. (RUN-503 modernized the default
  // skin to compose run-*; the old hand-rolled wireframe copy had diverged and,
  // after the headless switched to listening for run-tab-show, its hand-rolled
  // tabstrip no longer switched tabs at all.)
  static defaultSkin = defaultSkin;
  static skinLoaders = {};

  // ── Observed attributes ────────────────────────────────────────────────────
  static get observedAttributes() {
    return [
      'src',
      'active-tab',
      'tab-label-usage',
      'tab-label-style',
      'tab-label-code',
      'tab-label-accessibility',
    ];
  }

  // ── Arrow-function closure event handlers (private methods are not writable)
  _onTabShowBound = (e) => this.#handleTabShow(e);
  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  // ── Constructor ────────────────────────────────────────────────────────────
  constructor() {
    super();
  }

  // ── connectedCallback ──────────────────────────────────────────────────────
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'region');
    this.addEventListener('run-tab-show', this._onTabShowBound);
    this.#updateAria();
    if (this.#src) {
      this.#fetchManifest(this.#src);
    }
    this.requestUpdate();
  }

  // ── disconnectedCallback ───────────────────────────────────────────────────
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('run-tab-show', this._onTabShowBound);
    this.#abortFetch();
    this.#teardownSlotListeners();
  }

  // ── attributeChangedCallback ───────────────────────────────────────────────
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'src':                    this.src = newValue ?? ''; break;
      case 'active-tab':             this.activeTab = newValue ?? 'usage'; break;
      case 'tab-label-usage':        this.tabLabelUsage = newValue ?? 'Usage'; break;
      case 'tab-label-style':        this.tabLabelStyle = newValue ?? 'Style'; break;
      case 'tab-label-code':         this.tabLabelCode = newValue ?? 'Code'; break;
      case 'tab-label-accessibility': this.tabLabelAccessibility = newValue ?? 'Accessibility'; break;
    }
  }

  // ── Public property accessors ──────────────────────────────────────────────

  get src() { return this.#src; }
  set src(value) {
    const coerced = String(value ?? '').trim();
    if (this.#src === coerced) return;
    this.#src = coerced;
    this.#abortFetch();
    if (coerced) {
      this.#fetchManifest(coerced);
    } else {
      this.#manifest = null;
      this.#loadState = 'idle';
      this.#errorStatus = 0;
      this.#errorMessage = '';
      this.#updateAria();
      this.requestUpdate();
    }
  }

  get activeTab() { return this.#activeTab; }
  set activeTab(value) {
    const coerced = String(value ?? '').trim();
    const resolved = VALID_TABS.includes(coerced) ? coerced : 'usage';
    if (this.#activeTab === resolved) return;
    this.#activeTab = resolved;
    this.setAttribute('active-tab', resolved);
    this.#updateAria();
    this.requestUpdate();
  }

  get tabLabelUsage() { return this.#tabLabelUsage; }
  set tabLabelUsage(value) {
    const coerced = String(value ?? '').trim() || 'Usage';
    if (this.#tabLabelUsage === coerced) return;
    this.#tabLabelUsage = coerced;
    this.requestUpdate();
  }

  get tabLabelStyle() { return this.#tabLabelStyle; }
  set tabLabelStyle(value) {
    const coerced = String(value ?? '').trim() || 'Style';
    if (this.#tabLabelStyle === coerced) return;
    this.#tabLabelStyle = coerced;
    this.requestUpdate();
  }

  get tabLabelCode() { return this.#tabLabelCode; }
  set tabLabelCode(value) {
    const coerced = String(value ?? '').trim() || 'Code';
    if (this.#tabLabelCode === coerced) return;
    this.#tabLabelCode = coerced;
    this.requestUpdate();
  }

  get tabLabelAccessibility() { return this.#tabLabelAccessibility; }
  set tabLabelAccessibility(value) {
    const coerced = String(value ?? '').trim() || 'Accessibility';
    if (this.#tabLabelAccessibility === coerced) return;
    this.#tabLabelAccessibility = coerced;
    this.requestUpdate();
  }

  // ── Read-only accessors (consumed by skin render functions) ────────────────
  get manifest() { return this.#manifest; }
  get loadState() { return this.#loadState; }
  get errorStatus() { return this.#errorStatus; }
  get errorMessage() { return this.#errorMessage; }

  // ── updated() — post-skin host work only ───────────────────────────────────
  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.#setupSlotListeners();
    this.#wirePlaygrounds();
    this.#updateAria();
  }

  // ── Private: playground wiring ─────────────────────────────────────────────
  // The skin emits bare <run-playground> chrome; the playground's control
  // panel and live preview are driven by tag + contractData, which only the
  // headless can hand over (contractData is an object property — skins are
  // string templates). previewHtml seeds the preview with the manifest's
  // first usage example so slot content (e.g. a button label) is real.
  #wirePlaygrounds() {
    const m = this.#manifest;
    if (!m?.tag || !this.shadowRoot) return;
    this.shadowRoot.querySelectorAll('run-playground').forEach(pg => {
      if (pg.tag !== m.tag) pg.tag = m.tag;
      if (!pg.contractData && m.contract) {
        pg.contractData = {
          ...m.contract,
          status: m.status,
          previewHtml: m.usage?.examples?.[0]?.html,
        };
      }
    });
  }

  // ── Private: ARIA management ───────────────────────────────────────────────
  #updateAria() {
    const label = this.#manifest?.title
      ? `${this.#manifest.title} component page`
      : 'Component page';
    this.setAttribute('aria-label', label);
    this.toggleAttribute('data-loading', this.#loadState === 'loading');
    this.toggleAttribute('data-error', this.#loadState === 'error');
    this.toggleAttribute('data-loaded', this.#loadState === 'loaded');
    this.setAttribute('data-active-tab', this.#activeTab);
  }

  // ── Private: slot listener lifecycle ──────────────────────────────────────
  #setupSlotListeners() {
    if (!this.shadowRoot) return;
    const slots = this.shadowRoot.querySelectorAll('slot[name]');
    if (slots.length === 0 || this.#slotListenersAttached) return;
    slots.forEach(slot => {
      slot.addEventListener('slotchange', this._onSlotChangeBound);
      // Reflect initial assigned state
      const assigned = slot.assignedElements({ flatten: true });
      this.toggleAttribute(`data-has-${slot.name}`, assigned.length > 0);
    });
    this.#slotListenersAttached = true;
  }

  #teardownSlotListeners() {
    if (!this.shadowRoot || !this.#slotListenersAttached) return;
    this.shadowRoot.querySelectorAll('slot[name]').forEach(slot => {
      slot.removeEventListener('slotchange', this._onSlotChangeBound);
    });
    this.#slotListenersAttached = false;
  }

  // ── Private: event handlers ────────────────────────────────────────────────
  #handleTabShow(event) {
    const name = event.detail?.name;
    if (!name || !VALID_TABS.includes(name)) return;
    if (name === this.#activeTab) return;
    this.activeTab = name;
    this.#emitTabChange(name);
  }

  #handleSlotChange(event) {
    const slot = event.target;
    const assigned = slot.assignedElements({ flatten: true });
    this.toggleAttribute(`data-has-${slot.name}`, assigned.length > 0);
  }

  // ── Private: event emitters ────────────────────────────────────────────────
  #emitTabChange(tab) {
    this.dispatchEvent(new CustomEvent('run-tab-change', {
      bubbles: true,
      composed: true,
      detail: { tab },
    }));
  }

  #emitManifestLoaded(manifest) {
    this.dispatchEvent(new CustomEvent('run-manifest-loaded', {
      bubbles: true,
      composed: true,
      detail: { manifest },
    }));
  }

  #emitManifestError(src, status, message) {
    this.dispatchEvent(new CustomEvent('run-manifest-error', {
      bubbles: true,
      composed: true,
      detail: { src, status, message },
    }));
  }

  // ── Private: manifest fetch ────────────────────────────────────────────────
  async #fetchManifest(url) {
    this.#abortFetch();
    this.#abortController = new AbortController();
    this.#loadState = 'loading';
    this.#manifest = null;
    this.#errorStatus = 0;
    this.#errorMessage = '';
    this.#updateAria();
    this.requestUpdate();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal, cache: 'no-cache' });
      if (!response.ok) {
        this.#loadState = 'error';
        this.#errorStatus = response.status;
        this.#errorMessage = response.statusText || `HTTP ${response.status}`;
        this.#updateAria();
        this.requestUpdate();
        this.#emitManifestError(url, response.status, response.statusText || `HTTP ${response.status}`);
        return;
      }
      const manifest = await response.json();
      this.#manifest = manifest;
      this.#loadState = 'loaded';
      this.#updateAria();
      this.requestUpdate();
      this.#emitManifestLoaded(manifest);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.#loadState = 'error';
      this.#errorStatus = 0;
      this.#errorMessage = err.message;
      this.#updateAria();
      this.requestUpdate();
      this.#emitManifestError(url, 0, err.message);
    }
  }

  #abortFetch() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }
}

if (!customElements.get('run-component-page')) {
  customElements.define('run-component-page', RunComponentPage);
}

export { RunComponentPage };
export default RunComponentPage;

// Contracted constants — exported so tests and consumers can reference canonical
// values without duplicating them. Mirrors contract.md §COMPONENT_PAGE_TABS.
export const COMPONENT_PAGE_TABS = Object.freeze(['usage', 'style', 'code', 'accessibility']);
export const COMPONENT_PAGE_FETCH_STATES = Object.freeze(['idle', 'loading', 'loaded', 'error']);